import { Check } from 'lucide-react';
import { useDb } from '../../db/hooks';
import { useCanWrite } from '../../account/useCanWrite';
import { useSchedules } from '../../data/schedule';
import { useSessions } from '../../data/sessions';
import { clearDose, dialysisDates, logDose, useMedDoses, useMedications } from '../../data/meds';
import { dosesOn } from '../../lib/meds';
import { localToday } from '../sessions/fields';

/** Today's planned home doses with what's been logged. */
export function useTodayDoses() {
  const today = localToday();
  const { medications, isLoading } = useMedications();
  const { schedules, exceptions, isLoading: schedulesLoading } = useSchedules();
  const { sessions } = useSessions();
  const doses = useMedDoses(today, today);
  const dialysisToday = dialysisDates(schedules, exceptions, sessions, today, today).has(today);
  const logged = new Map(doses.map((dose) => [dose.id, dose.status]));
  return {
    planned: dosesOn(medications, today, dialysisToday).map((dose) => ({ ...dose, status: logged.get(dose.doseId) ?? null })),
    hasMedications: medications.some((med) => med.active),
    isLoading: isLoading || schedulesLoading
  };
}

/** Tap a dose to mark it taken (tap again to undo); "Skip" records a skipped dose. */
export function DosesToday({ compact = false }: { compact?: boolean }) {
  const db = useDb();
  const canWrite = useCanWrite();
  const { planned } = useTodayDoses();
  if (!planned.length) return <p className="text-sm text-clinical-muted">Nothing to take today.</p>;
  return (
    <ul className="divide-y divide-clinical-line">
      {planned.map((dose) => {
        const label = [dose.med.name, dose.med.strength].filter(Boolean).join(' ');
        const taken = dose.status === 'taken';
        return (
          <li key={dose.doseId} className="flex items-center gap-2 py-1.5">
            <button
              type="button"
              disabled={!canWrite}
              aria-pressed={taken}
              aria-label={`${label} at ${dose.time}: ${taken ? 'taken' : 'mark as taken'}`}
              onClick={() => void (taken ? clearDose(db, dose.doseId) : logDose(db, dose, 'taken'))}
              className="focus-ring flex min-h-11 flex-1 items-center gap-3 rounded-lg text-left text-sm"
            >
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 ${
                  taken ? 'border-clinical-primary bg-clinical-primary text-white' : 'border-clinical-line'
                }`}
              >
                {taken ? <Check className="h-4 w-4" /> : null}
              </span>
              <span className="w-12 shrink-0 font-medium tabular-nums">{dose.time}</span>
              <span className={`min-w-0 flex-1 ${taken ? 'text-clinical-muted line-through' : ''}`}>
                {label}
                {!compact && (dose.med.dose || dose.med.with_meals) ? (
                  <span className="block text-xs text-clinical-muted no-underline">{[dose.med.dose, dose.med.with_meals ? 'with food' : null].filter(Boolean).join(' · ')}</span>
                ) : null}
              </span>
            </button>
            {canWrite && !taken ? (
              dose.status === 'skipped' ? (
                <button type="button" onClick={() => void clearDose(db, dose.doseId)} className="focus-ring min-h-11 rounded-lg px-2 text-xs font-medium text-clinical-muted">
                  Skipped · undo
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void logDose(db, dose, 'skipped')}
                  aria-label={`Skip ${label} at ${dose.time}`}
                  className="focus-ring min-h-11 rounded-lg px-2 text-xs font-medium text-clinical-muted hover:bg-clinical-surface"
                >
                  Skip
                </button>
              )
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
